
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useLanguage } from "@/contexts/LanguageContext";

interface Team {
  id: string;
  name: string;
  description: string | null;
  image_url?: string;
}

interface Mission {
  id: string;
  title: string;
  status: string;
}

const Missions = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useSupabaseUser();
  const { isAdmin } = useUserRoles();
  const { language } = useLanguage();

  const { data: teams, isLoading } = useQuery({
    queryKey: ['teams-with-missions', user?.id],
    queryFn: async () => {
      if (!user) return [];

      // Fetch all teams and their missions for all users, including team logos
      const { data, error } = await supabase
        .from('teams')
        .select(`
          id,
          name,
          description,
          image_url,
          missions:missions(
            id,
            title,
            status
          )
        `);

      if (error) {
        toast({
          title: "Error loading teams",
          description: error.message,
          variant: "destructive",
        });
        throw error;
      }

      console.log("Teams data with logos:", data);
      return data as (Team & { missions: Mission[] })[];
    },
    enabled: !!user,
  });

  return (
    <AppLayout>
      <div className="space-y-8 animate-fadeIn px-8 py-8">
        <div className="flex justify-between items-center">
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-secondary-800">
              {language === "en" ? "Work Instructions Overview" : "Επισκόπηση Οδηγιών Εργασίας"}
            </h1>
            <p className="text-gray-500">
              {language === "en" 
                ? "View and manage all work instructions across all teams" 
                : "Δείτε και διαχειριστείτε όλες τις οδηγίες εργασίας ασφαλείας σε όλες τις ομάδες"}
            </p>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                    <div className="space-y-2">
                      <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                      <div className="h-9 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teams?.map((team) => {
              console.log(`Team ${team.name} logo URL:`, team.image_url);
              return (
                <Card key={team.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-50 text-primary-600 overflow-hidden">
                        {team.image_url ? (
                          <img 
                            src={team.image_url} 
                            alt={`${team.name} logo`}
                            className="w-full h-full object-contain"
                            onError={(e) => {
                              console.error(`Failed to load image for team ${team.name}:`, team.image_url);
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-slate-400 to-slate-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                            {team.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-lg font-medium text-secondary-800">
                          {language === "en" 
                            ? `${team.name} Work Instructions` 
                            : `${team.name} Οδηγίες Εργασίας`}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {language === "en"
                            ? `Active Instructions: ${team.missions.filter(m => m.status === 'active').length}`
                            : `Ενεργές Οδηγίες: ${team.missions.filter(m => m.status === 'active').length}`}
                        </p>
                        <Button 
                          className="w-full"
                          onClick={() => navigate(`/missions/team/${team.id}`)}
                        >
                          {language === "en" 
                            ? "View Work Instructions" 
                            : "Προβολή Οδηγιών Εργασίας"}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default Missions;
